import { useState, useEffect, useMemo, useCallback } from "react";

import { ServiceTypeSelectorOption } from "../types/order";

/**
 * Custom hook for managing service type in sessionStorage with cross-tab synchronization.
 * 
 * @param key - SessionStorage key for storing the service type
 * @param initialValue - Default service type if none is stored
 * @returns Object containing current service and management functions
 * 
 * @example
 * const { currentService, updateService, clearService, isCurrentService } = 
 *   useServiceStorage("selectedService", ServiceTypeSelectorOption.RENTAL);
 */
const useServiceStorage = (
  key: string,
  initialValue: ServiceTypeSelectorOption
) => {
  const [service, setService] = useState<ServiceTypeSelectorOption>(() => {
    // Server-side rendering guard
    if (typeof window === "undefined") return initialValue;

    try {
      const stored = sessionStorage.getItem(key);
      
      // Validate stored value against enum
      if (stored && Object.values(ServiceTypeSelectorOption).includes(stored as ServiceTypeSelectorOption)) {
        return stored as ServiceTypeSelectorOption;
      }
      
      return initialValue;
    } catch (error) {
      console.error("SessionStorage access error:", error);
      return initialValue;
    }
  });

  /**
   * Updates the service type in both state and sessionStorage
   */
  const updateService = useCallback(
    (newService: ServiceTypeSelectorOption) => {
      try {
        setService(newService);
        sessionStorage.setItem(key, newService);
      } catch (error) {
        console.error("Failed to update service:", error);
      }
    },
    [key]
  );

  /**
   * Clears the service type from both state and sessionStorage
   */
  const clearService = useCallback(() => {
    try {
      setService(initialValue);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error("Failed to clear service:", error);
    }
  }, [key, initialValue]);

  /**
   * Checks if the given service type matches the current service
   */
  const isCurrentService = useCallback(
    (type: ServiceTypeSelectorOption) => service === type,
    [service]
  );

  // Synchronize service type across browser tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue && event.newValue !== service) {
        const newValue = event.newValue as ServiceTypeSelectorOption;
        
        // Validate before updating
        if (Object.values(ServiceTypeSelectorOption).includes(newValue)) {
          setService(newValue);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key, service]);

  // Memoize return value to prevent new object reference on every render
  return useMemo(
    () => ({
      currentService: service,
      updateService,
      clearService,
      isCurrentService,
    }),
    [service, updateService, clearService, isCurrentService]
  );
};

export default useServiceStorage;